import type { MultiPayerPaymentRequestAttachment } from "Components/Chat/chatAttachmentTypes";
import { identityUserPublicIdFromStreamId } from "Components/Chat/chatConstants";

/**
 * What the viewer may do with a multi-payer request card.
 *
 * - `closed`: the author closed it (their own flag in the attachment).
 * - `closable`: the viewer IS the author and may close it.
 * - `payable`: the viewer may pay it; carries the only recipient the money may go to.
 * - `unavailable`: no action — the author is unknown or the attachment names someone else.
 */
export type MultiPayerRequestState =
  | { kind: "closed" }
  | { kind: "closable" }
  | { kind: "payable"; recipientIdentityUserPublicID: string }
  | { kind: "unavailable" };

/**
 * The money goes to the message AUTHOR, never to whoever the attachment names. The attachment is
 * client-written (any member can post one through the Stream API with any recipient id), while
 * the author id is bound to the Stream token and cannot be forged. The backend gate only requires
 * the recipient to be an accepted member, so a request naming another member would route the
 * payers' money there while the card shows the author's name. The attachment's recipient is
 * therefore only cross-checked against the author: when they differ the card is not a request
 * and offers no payment (ADR-0009).
 */
export const resolveMultiPayerRequestState = (
  attachment: MultiPayerPaymentRequestAttachment,
  authorStreamId: string | undefined,
  viewerStreamId: string | undefined,
): MultiPayerRequestState => {
  if (attachment.isClosed) {
    return { kind: "closed" };
  }

  if (authorStreamId === undefined) {
    return { kind: "unavailable" };
  }

  if (authorStreamId === viewerStreamId) {
    return { kind: "closable" };
  }

  const recipientIdentityUserPublicID =
    identityUserPublicIdFromStreamId(authorStreamId);
  if (
    attachment.recipientIdentityUserPublicID !== recipientIdentityUserPublicID
  ) {
    return { kind: "unavailable" };
  }

  return { kind: "payable", recipientIdentityUserPublicID };
};
